import JSZip, { JSZipObject } from 'jszip';

import { DirectoryWriter } from './Filesystem/Container/DirectoryWriter';
import { ImportFileArg } from './AppAction';
import { _fakeMessage, BaseMessage, RawBaseMassage } from './Communication';
import { appInfo } from './AppInfo';
import { findAllFiles, readBinaryFile, writeFile } from './Filesystem/Helper';
import { remove, list } from './Filesystem/Wrapper';
import { share } from './Share';

import { downloadBlob, getContent } from '@egr/xbox/utils/Download';
import { getApp } from '@egr/xbox/base-app/App';
import { getEnvBoolean } from '@egr/xbox/utils/ReactScriptHelper';
import { getUUID } from '@egr/xbox/utils/Helper';
import { rejectValue } from '@egr/xbox/utils/Promise';
import { stripDoubleSlashesFromPath } from '@egr/xbox/utils/Path';

import { Lock, LockContext } from '@egr/wcf/modules/utils/async';
import { isNullOrEmpty } from '@egr/wcf/modules/utils/string';

interface SynchronizeWebaction extends BaseMessage {
    type: 'webaction';
    action: 'synchronize';
    arguments: 'background' | 'foreground';
}

export function synchronizeWebAction(value: 'background' | 'foreground'): void {
    _fakeMessage({
        transaction_id: -1,
        type: 'webaction',
        action: 'synchronize',
        arguments: value
    } as SynchronizeWebaction & RawBaseMassage);
}

export function emulateBackButtonPress(): void {
    _fakeMessage({
        transaction_id: -1,
        type: 'webaction',
        action: 'userinteraction',
        arguments: 'back_pressed'
    });
}

export async function zipDirectory(path: string): Promise<ArrayBuffer> {
    const l: number = path.length;
    const zip: JSZip = new JSZip();

    for await (const file of findAllFiles(path)) {
        const content: ArrayBuffer = await readBinaryFile(file);
        zip.file(file.substr(l), content);
    }

    return zip.generateAsync({type: 'arraybuffer'});
}

export async function unzip(content: ArrayBuffer, path: string): Promise<void> {
    const zip: JSZip = new JSZip();
    await zip.loadAsync(content);

    const container: DirectoryWriter = new DirectoryWriter(path);
    const lock: Lock = new Lock();
    const unzipFile: (zipPath: string, file: JSZipObject) => Promise<void> = async (zipPath: string, file: JSZipObject): Promise<void> => {
        const lockContext: LockContext = await lock.acquireLock();
        try {
            const fileContent: ArrayBuffer = await file.async('arraybuffer');
            await container.addFile(fileContent, zipPath);
        } finally {
            lockContext.unlock();
        }
    };

    const promises: Array<Promise<void>> = [];
    zip.forEach((relPath: string, file: JSZipObject): void => {
        if (relPath.startsWith('.')) {
            // ignore hidden files like `.DS_Store`
            return;
        }

        if (!relPath.endsWith('/')) {
            promises.push(unzipFile(relPath, file));
        }
    });

    await Promise.all(promises);
}

export async function openInputDialog(callback: (input: HTMLInputElement) => Promise<void>, accept: string = '.zip'): Promise<void> {
    return new Promise((resolve: VoidFunction, reject: (error: Error) => void): void => {
        const attachInput: boolean = getEnvBoolean('E2E_ATTACH_OPEN_FILE_INPUT', false);
        const input: HTMLInputElement = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.onchange = async (): Promise<void> => {
            try {
                await callback(input);
                resolve();
            } catch (error) {
                reject(error);
            } finally {
                if (attachInput) {
                    document.body.removeChild(input);
                }
            }
        };

        if (attachInput) {
            input.id = 'e2e-openFile-input';
            document.body.appendChild(input);
        } else {
            input.click();
        }
    });
}

export function openFile(args?: ImportFileArg, delayWebAction?: Promise<void>): Promise<void> {
    const accept: string = (args != null && args.file_types != null) ? args.file_types.join(',') : '.pec,.pbox,.obk,.eox';
    return openInputDialog(
        async (input: HTMLInputElement): Promise<void> => {
            let importPath: string;
            const fileList: FileList | null = input.files;
            if (fileList == null || fileList.length < 1) {
                throw new Error('no files');
            }

            const extension: string  = fileList[0].name.split('.').slice(-1)[0].toLocaleLowerCase();
            if (extension === 'pbox') {
                importPath = '/imports/' + getUUID() + '/';
                const promises: Array<Promise<void>> = [];
                let zip: JSZip = new JSZip();
                zip = await zip.loadAsync(fileList[0]);
                zip.forEach((path: string, file: JSZipObject): void => {
                    if (file.dir) {
                        return;
                    }

                    promises.push((async (): Promise<void> => {
                        return writeFile(
                            stripDoubleSlashesFromPath(importPath + path),
                            await file.async('arraybuffer')
                        );
                    })());
                });

                await Promise.all<void>(promises);
            } else {
                importPath = '/imports/' + getUUID() + '.' + extension;
                await writeFile(
                    importPath,
                    fileList[0]
                );
            }

            const fileExtension: string | undefined = fileList[0].name.split('.').pop();
            if (isNullOrEmpty(fileExtension)) {
                throw new Error('empty file extension');
            }

            const triggerWebAction: VoidFunction = (): void => {
                // ToDo: use  new app api module
                _fakeMessage({
                    transaction_id: -1,
                    type: 'webaction',
                    action: 'openfile',
                    arguments: {
                        type: fileExtension.toLocaleLowerCase(),
                        path: importPath,
                        context: args != null ? args.context : undefined
                    }
                });
            };

            if (delayWebAction != null) {
                void (async (): Promise<void> => {
                    await delayWebAction;
                    triggerWebAction();
                })();
            } else {
                triggerWebAction();
            }
        },
        accept
    );
}

export async function exportRootFolder(filename: string = 'root'): Promise<void> {
    const exportPath: string = '/tmp/export_root.zip';

    await rejectValue(remove(exportPath), undefined);
    const content: ArrayBuffer = await zipDirectory('/');

    if (appInfo == null) {
        downloadBlob(
            new Blob(
                [new Uint8Array(content)],
                {
                    type: 'application/octet-stream'
                }
            ),
            filename + '.zip'
        );
    } else {
        try {
            await writeFile(exportPath, content);
            await share(
                {
                    path: exportPath,
                    contentType: 'application/zip',
                    filename: filename + '.zip',
                    remove_path_after_share: true
                },
                true,
                true
            );
        } catch {
            await rejectValue(remove(exportPath), undefined);
        }
    }
}

async function importZip(url: string, path: string): Promise<void> {
    const content: ArrayBuffer = await getContent(url);
    await unzip(content, path);
}

export function importProject(url: string): Promise<void> {
    return importZip(url, '/projects/' + getUUID() + '/');
}

export async function importRootFolder(url?: string): Promise<void> {
    const rootItems: Array<string> = await list('/');
    if (rootItems.length) {
        throw new Error('Root folder cannot be imported');
    }

    if (isNullOrEmpty(url)) {
        return openInputDialog(async (input: HTMLInputElement) => {
            const file: File | undefined = input.files?.[0];
            if (file != null) {
                await unzip(
                    await file.arrayBuffer(),
                    '/'
                );
            }
        });
    }

    return importZip(url, '/');
}

export async function emulateWebglContextLost(): Promise<void> {
    const app  = await getApp();
    const looseContext = app.sceneManager.getViewer().app.engine._gl.getExtension('WEBGL_lose_context');

    looseContext?.loseContext();
    setTimeout(() => {
        looseContext?.restoreContext();
    }, 3000);
}
